import { CheckCircleIcon, Link, WarningIcon } from '@chakra-ui/icons'
import { IhiValidationResult } from './PatientFormSlice'
import { IHIRecord } from '../../types'
import {
  HIServiceMessage,
  PatientIhiSearchErrorResponse,
  PatientIhiSearchSuccessfulResponse,
} from '../../features/patient/PatientContextSlice'
import { Text } from '@chakra-ui/react'

export const formatIHIValidate = ({
  ihi_status,
  ihi_number,
  service_message,
  isLoading,
  record_status,
  warning,
  callback,
  customError,
}: IhiValidationResult & {
  isLoading?: boolean
  warning?: string | React.ReactNode
  callback?: () => void
  customError?: string | React.ReactNode
}) => {
  if ((!ihi_status && !service_message?.codeField && !warning) || isLoading) {
    return {
      message: '',
      messageColor: 'gray.500',
      color: '',
      icon: '',
      isAcceptable: false,
    }
  }

  if (ihi_status === 'Retired' || ihi_status === 'Expired') {
    if (!ihi_number) {
      return {
        message: warning || 'No IHI verified for this patient.',
        messageColor: 'red.500',
        color: 'red',
        icon: <WarningIcon color="red.500" />,
        isAcceptable: false,
      }
    }
  }

  if (service_message?.codeField === '01611') {
    return {
      message: customError ?? (
        <Text>
          Sorry, validation timed out. Please click{' '}
          <Link onClick={callback} color="blue.500">
            here
          </Link>{' '}
          to try again.
        </Text>
      ),
      messageColor: 'red.500',
      color: 'red',
      icon: <WarningIcon color="red.500" />,
      isAcceptable: false,
    }
  }

  if (service_message?.codeField === '01439') {
    return {
      message: warning || 'IHI number is not found.',
      messageColor: 'red.500',
      color: 'red',
      icon: <WarningIcon color="red.500" />,
      isAcceptable: false,
    }
  }

  if (ihi_status === 'Active' && record_status === 'Verified') {
    return {
      message: warning || 'Validation is successful!',
      messageColor: 'gray',
      color: 'green',
      icon: <CheckCircleIcon color="green.500" />,
      isAcceptable: true,
    }
  }

  if (warning) {
    return {
      message: warning,
      messageColor: 'red.500',
      color: 'red',
      icon: <WarningIcon color="red.500" />,
      isAcceptable: false,
    }
  }

  return {
    message: 'No IHI verified or no IHI status for this patient.',
    messageColor: 'yellow.500',
    color: ' #D69E2E',
    icon: <WarningIcon color="yellow.500" />,
    isAcceptable: false,
  }
}

export const getWarning = (
  newIhiRecord: Omit<IHIRecord, 'created_at'>,
  originalIhiRecord?: Omit<IHIRecord, 'created_at'>
) => {
  if (
    (newIhiRecord.ihi_status === 'Retired' || newIhiRecord.ihi_status === 'Expired') &&
    !originalIhiRecord
  ) {
    return `This IHI record has an '${newIhiRecord.ihi_status}' IHI status and cannot be retrieved.`
  }

  if (newIhiRecord.service_message?.codeField === '01611') {
    return 'Ihi validation timed out, trying again...'
  }

  if (!newIhiRecord.ihi_number) {
    return `No Healthcare Individual record have been found.`
  }

  if (newIhiRecord.record_status !== 'Verified') {
    return `Sorry, we can not store the IHI number with a ${newIhiRecord.record_status} record status.`
  }

  if (
    originalIhiRecord?.ihi_status !== newIhiRecord.ihi_status &&
    (originalIhiRecord?.ihi_status === 'Active' || originalIhiRecord?.ihi_status === 'Deceased')
  ) {
    return `The number status changed from ${originalIhiRecord.ihi_status} to ${newIhiRecord.ihi_status}.`
  }
  return ''
}

interface NewIhiRecord extends Omit<IHIRecord, 'created_at'> {
  service_message: HIServiceMessage | null
}

export const formatIhiResponse = (
  response: PatientIhiSearchSuccessfulResponse | PatientIhiSearchErrorResponse,
  originalIhiRecord?: Omit<IHIRecord, 'created_at'>
) => {
  let newRecord: NewIhiRecord
  if ((response as PatientIhiSearchErrorResponse).Message) {
    const message = (response as PatientIhiSearchErrorResponse).Message
    if (message.startsWith('01613')) {
      newRecord = {
        ihi_number: originalIhiRecord?.ihi_number || '',
        ihi_status: 'Expired',
        record_status: originalIhiRecord?.record_status || 'Unverified',
        service_message: null,
      }
    } else if (message.startsWith('01614')) {
      newRecord = {
        ihi_number: originalIhiRecord?.ihi_number || '',
        ihi_status: 'Retired',
        record_status: originalIhiRecord?.record_status || 'Unverified',
        service_message: null,
      }
    } else {
      /// Fixme: Should handler error
      newRecord = {
        ihi_number: '',
        ihi_status: 'Active',
        record_status: 'Unverified',
        service_message: null,
      }
    }
  } else {
    response = response as PatientIhiSearchSuccessfulResponse
    newRecord = {
      ihi_number: response.IHI,
      ihi_status: response.HIStatus,
      record_status: response.RecordStatus,
      service_message: response.ServiceMessages?.serviceMessageField[0] || null,
    }
  }
  return newRecord
}

export const formatIHIRecord = ({
  medicareValidationResult,
  dvaValidationResult,
  ihiValidationResult,
}: {
  medicareValidationResult: IhiValidationResult
  dvaValidationResult: IhiValidationResult
  ihiValidationResult: IhiValidationResult
}) => {
  const isMedicareAcceptable = formatIHIValidate(medicareValidationResult).isAcceptable
  const isDvaAcceptable = formatIHIValidate(dvaValidationResult).isAcceptable
  const isIhiAcceptable = formatIHIValidate(ihiValidationResult).isAcceptable

  if (isMedicareAcceptable) {
    return {
      ihi_status: medicareValidationResult.ihi_status ?? '',
      record_status: medicareValidationResult.record_status ?? '',
      ihi_number: medicareValidationResult.ihi_number ?? '',
    }
  }
  if (isDvaAcceptable) {
    return {
      ihi_status: dvaValidationResult.ihi_status ?? '',
      record_status: dvaValidationResult.record_status ?? '',
      ihi_number: dvaValidationResult.ihi_number ?? '',
    }
  }
  if (isIhiAcceptable) {
    return {
      ihi_status: ihiValidationResult.ihi_status ?? '',
      record_status: ihiValidationResult.record_status ?? '',
      ihi_number: ihiValidationResult.ihi_number ?? '',
    }
  }
  return null
}
